// Waveform mipmap computation (P2.4) — Architecture §5 stage 1.
//
// Pure and synchronous, with no reference to `self`, `postMessage` or any worker global. The
// worker entry is a thin wrapper around this file, which is what makes the arithmetic testable in
// Node against a hand-computed fixture — the thing P2.4's VERIFY actually asks for. A summariser
// that can only be exercised by starting a worker is a summariser nobody checks the numbers of.
//
// **One pass over the PCM, with the coarser levels folded inline.**
//
// The obvious implementation computes each level from the samples: six passes over 17 million
// frames. The next-most-obvious computes level 0 from the samples and each coarser level from the
// one below it: one pass plus five cheap ones, but the coarse levels only exist after the fine
// ones are finished. Neither is what this does. Here, finishing a level-0 bucket immediately folds
// it into level 1's accumulator; four of those finish a level-1 bucket, which folds into level 2;
// and so on up. Every level advances together, in lockstep with a single read of the samples.
//
// The fold work is amortised into nothing: a level-1 fold happens once per 32 frames, level 2 once
// per 128, and the whole tail sums to a third of one extra pass over the *buckets*, not the
// samples. Reading the PCM is the entire cost, and it is paid once.
//
// **A note on "emit the coarsest level first", which P2.4 asks for and which is subtler than it
// looks.** A *complete* coarsest level cannot exist before the last sample has been read — its
// final bucket covers the last 32,768 frames of the track. No ordering trick changes that. What
// this file gives instead is the property that actually serves FR-11: at any moment, every level
// is final for the first `FRAMES_DONE` frames, so a renderer can draw a correct partial waveform
// that fills in left to right rather than waiting. The ready bits are then set coarsest-first at
// the end, so a reader polling the mask sees the overview before the ribbon. The honest summary is
// that progressiveness here comes from the prefix property, not from the level ordering.

import {
  bucketFrameCount,
  computeWaveformMipmapLayout,
  WAVEFORM_BUCKET_FLOATS,
  WAVEFORM_MIPMAP_HEADER,
  WAVEFORM_MIPMAP_MAGIC,
  WAVEFORM_MIPMAP_VERSION,
  type WaveformMipmapLayout,
  type WaveformMipmapSpec,
  withLevelReady,
} from '../../platform/types/waveform-mipmap';

/**
 * How often `FRAMES_DONE` is published, in frames.
 *
 * 65,536 is two coarsest buckets, so a progress tick always lands on a bucket boundary at *every*
 * level simultaneously. That turns the counter into a promise a renderer can act on without
 * further arithmetic: "every level is complete and final for frames `[0, FRAMES_DONE)`". A tick
 * size that was not a multiple of 32,768 would leave a partially-accumulated bucket straddling the
 * boundary at some level, and the promise would be false exactly at the edge somebody draws.
 *
 * ~1.4 s of audio at 48 kHz, which at the measured throughput is a tick every few milliseconds.
 */
export const MIPMAP_PROGRESS_INTERVAL_FRAMES = 65_536;

/** A level's running state. One object per level, reused for every bucket — six allocations for
 * the whole track, rather than one per bucket. */
interface LevelAccumulator {
  min: number;
  max: number;
  /** Sum of squared samples. Kept rather than a running RMS so folding into the parent is exact:
   * RMS is not additive, sums of squares are. */
  sumSquares: number;
  /** Frames folded in so far — the weight this bucket carries to its parent. */
  frames: number;
  /** Child buckets folded in so far; at 4 the bucket is complete (the ladder's 4× step). */
  children: number;
  /** Next bucket index to write at this level. */
  bucket: number;
  /** The destination view, one per level. */
  view: Float32Array;
}

export interface MipmapResult {
  readonly layout: WaveformMipmapLayout;
  /** Wall-clock milliseconds spent summarising. Reported rather than logged: FR-11's ≤2 s budget
   * is a number somebody has to be able to read, and P2.4's VERIFY says to state it. */
  readonly elapsedMillis: number;
}

/**
 * Allocates a buffer for a track's mipmaps and returns it with its layout.
 *
 * `SharedArrayBuffer` when the page is cross-origin isolated, which it always is in production
 * (ADR-3) — the point is that the main thread can read completed levels *while* the worker is
 * still filling the rest, which a transferred `ArrayBuffer` cannot do (transferring it detaches
 * it from the sender). Falls back to a plain `ArrayBuffer` so this file stays runnable under
 * plain Node in tests, where `SharedArrayBuffer` exists but sharing is beside the point.
 */
export function createWaveformMipmapBuffer(spec: WaveformMipmapSpec): {
  readonly buffer: ArrayBufferLike;
  readonly layout: WaveformMipmapLayout;
} {
  const layout = computeWaveformMipmapLayout(spec);
  const buffer =
    typeof SharedArrayBuffer === 'function'
      ? new SharedArrayBuffer(layout.totalBytes)
      : new ArrayBuffer(layout.totalBytes);

  return { buffer, layout };
}

/** Writes the fixed header fields. Plain stores: nothing reads this buffer until the caller has
 * shared it, and the two fields that *are* read concurrently are atomic and written later. */
function writeHeader(target: ArrayBufferLike, layout: WaveformMipmapLayout): void {
  const header = new Uint32Array(target, 0, WAVEFORM_MIPMAP_HEADER.FRAMES_DONE / 4 + 1);

  header[WAVEFORM_MIPMAP_HEADER.MAGIC / 4] = WAVEFORM_MIPMAP_MAGIC;
  header[WAVEFORM_MIPMAP_HEADER.VERSION / 4] = WAVEFORM_MIPMAP_VERSION;
  header[WAVEFORM_MIPMAP_HEADER.FRAME_COUNT / 4] = layout.frameCount;
  header[WAVEFORM_MIPMAP_HEADER.SAMPLE_RATE_HZ / 4] = Math.round(layout.sampleRateHz);
  header[WAVEFORM_MIPMAP_HEADER.LEVEL_COUNT / 4] = layout.levels.length;
  header[WAVEFORM_MIPMAP_HEADER.LEVELS_READY / 4] = 0;
  header[WAVEFORM_MIPMAP_HEADER.FRAMES_DONE / 4] = 0;
}

/** An accumulator reset to the identity for min/max/sum. */
function resetAccumulator(accumulator: LevelAccumulator): void {
  accumulator.min = Number.POSITIVE_INFINITY;
  accumulator.max = Number.NEGATIVE_INFINITY;
  accumulator.sumSquares = 0;
  accumulator.frames = 0;
  accumulator.children = 0;
}

/**
 * Writes one finished bucket and folds it into the level above.
 *
 * Recursive, and bounded by the ladder length (six), so the depth is a constant known at compile
 * time rather than a function of the track. A 20-minute track recurses exactly as deep as a
 * 20-second one.
 */
function flushBucket(
  levels: LevelAccumulator[],
  levelIndex: number,
  layout: WaveformMipmapLayout,
): void {
  const accumulator = levels[levelIndex];
  const level = layout.levels[levelIndex];

  if (accumulator === undefined || level === undefined) {
    return;
  }
  if (accumulator.bucket >= level.bucketCount) {
    // Nothing left to write at this level. Reached when the track's tail has already filled the
    // last bucket and a parent flush cascades past it.
    return;
  }

  const empty = accumulator.frames === 0;
  const base = accumulator.bucket * WAVEFORM_BUCKET_FLOATS;

  // A bucket with no frames writes zeros rather than ±Infinity. Only reachable for a zero-length
  // track, but ±Infinity in a vertex buffer is a black screen rather than a flat line, and a flat
  // line is the honest picture of no audio.
  accumulator.view[base] = empty ? 0 : accumulator.min;
  accumulator.view[base + 1] = empty ? 0 : accumulator.max;
  accumulator.view[base + 2] = empty
    ? 0
    : Math.sqrt(accumulator.sumSquares / accumulator.frames);

  const parent = levels[levelIndex + 1];
  if (parent !== undefined && !empty) {
    if (accumulator.min < parent.min) {
      parent.min = accumulator.min;
    }
    if (accumulator.max > parent.max) {
      parent.max = accumulator.max;
    }
    // Sums of squares add; RMS does not. Carrying the sum rather than re-squaring the child's
    // written RMS is what keeps a coarse bucket exactly equal to the direct computation over the
    // same frames — which is the property the fixture test pins.
    parent.sumSquares += accumulator.sumSquares;
    parent.frames += accumulator.frames;
    parent.children += 1;

    if (parent.children === 4) {
      flushBucket(levels, levelIndex + 1, layout);
    }
  }

  accumulator.bucket += 1;
  resetAccumulator(accumulator);
}

/**
 * Summarises decoded channels into `target`, in place.
 *
 * Channels are **mono-summed** first. UX §3.1 draws one ribbon per deck, not a split stereo pair,
 * so a per-channel mipmap would double the memory to carry a distinction nothing renders. Worth
 * knowing rather than discovering: a mono sum cancels out-of-phase stereo content, so a track with
 * a deliberately phase-inverted channel draws quieter than it sounds. That is the same compromise
 * every DJ waveform makes, and the fix if it ever matters is a fourth float per bucket (side
 * energy), not a second mipmap.
 */
export function computeWaveformMipmaps(
  channels: readonly Float32Array[],
  layout: WaveformMipmapLayout,
  target: ArrayBufferLike,
  onProgress?: (framesDone: number) => void,
): MipmapResult {
  const startedAt = Date.now();

  if (target.byteLength < layout.totalBytes) {
    throw new RangeError(
      `waveform mipmap: target is ${String(target.byteLength)} bytes, layout needs ${String(layout.totalBytes)}`,
    );
  }

  writeHeader(target, layout);

  const levels: LevelAccumulator[] = layout.levels.map((level) => ({
    min: Number.POSITIVE_INFINITY,
    max: Number.NEGATIVE_INFINITY,
    sumSquares: 0,
    frames: 0,
    children: 0,
    bucket: 0,
    view: new Float32Array(
      target,
      level.byteOffset,
      level.bucketCount * WAVEFORM_BUCKET_FLOATS,
    ),
  }));

  const finest = levels[0];
  const finestLevel = layout.levels[0];
  const { frameCount } = layout;

  if (finest !== undefined && finestLevel !== undefined && frameCount > 0) {
    const channelCount = channels.length;
    const left = channels[0];
    const right = channels[1];
    const inverseChannels = 1 / Math.max(1, channelCount);
    const bucketFrames = finestLevel.bucketFrames;

    let inBucket = 0;
    let nextProgressAt = MIPMAP_PROGRESS_INTERVAL_FRAMES;

    for (let frame = 0; frame < frameCount; frame += 1) {
      // Stereo and mono are unrolled because they are every real track. The general path exists
      // so a 5.1 file decodes to *something* rather than throwing, not because it is fast.
      let sample: number;
      if (channelCount === 2 && left !== undefined && right !== undefined) {
        sample = ((left[frame] ?? 0) + (right[frame] ?? 0)) * 0.5;
      } else if (channelCount === 1 && left !== undefined) {
        sample = left[frame] ?? 0;
      } else {
        let sum = 0;
        for (let channel = 0; channel < channelCount; channel += 1) {
          sum += channels[channel]?.[frame] ?? 0;
        }
        sample = sum * inverseChannels;
      }

      if (sample < finest.min) {
        finest.min = sample;
      }
      if (sample > finest.max) {
        finest.max = sample;
      }
      finest.sumSquares += sample * sample;
      finest.frames += 1;

      inBucket += 1;
      if (inBucket === bucketFrames) {
        flushBucket(levels, 0, layout);
        inBucket = 0;
      }

      if (frame + 1 === nextProgressAt) {
        // Published only on a tick boundary, which is a multiple of the coarsest bucket, so the
        // "every level is final below this frame" promise in the constant's doc comment holds.
        publishFramesDone(target, frame + 1);
        onProgress?.(frame + 1);
        nextProgressAt += MIPMAP_PROGRESS_INTERVAL_FRAMES;
      }
    }

    // The tail. Every level may hold a partial bucket, and they must be flushed finest-first so
    // each one folds into a parent that has not been flushed yet.
    for (let index = 0; index < levels.length; index += 1) {
      const accumulator = levels[index];
      if (accumulator !== undefined && accumulator.frames > 0) {
        flushBucket(levels, index, layout);
      }
    }
  } else {
    // A zero-length track still gets a valid, readable buffer. `computeWaveformMipmapLayout`
    // gives it zero buckets at every level, so there is nothing to write and nothing to fold —
    // but the header is already correct, and that is what a reader validates.
  }

  publishFramesDone(target, frameCount);
  onProgress?.(frameCount);

  // Coarsest-first, per P2.4's rule. With a single-pass cascade every level finished at the same
  // instant, so this ordering costs nothing and buys the reader a sane polling story: the overview
  // strip's level is legible before the ribbon's, in the order a UI wants to draw them.
  let readyMask = 0;
  for (let index = layout.levels.length - 1; index >= 0; index -= 1) {
    readyMask = withLevelReady(readyMask, index);
    publishLevelsReady(target, readyMask);
  }

  return { layout, elapsedMillis: Date.now() - startedAt };
}

/**
 * Publishes progress atomically.
 *
 * `Atomics.store` rather than a plain write, and not as ceremony: this is the release half of the
 * pair that lets a reader on another thread look at buckets this thread is still writing. The
 * float stores above must not be reordered past it, which is exactly the guarantee a plain store
 * does not give and this one does.
 */
function publishFramesDone(target: ArrayBufferLike, framesDone: number): void {
  const header = new Int32Array(target, 0, WAVEFORM_MIPMAP_HEADER.FRAMES_DONE / 4 + 1);
  Atomics.store(header, WAVEFORM_MIPMAP_HEADER.FRAMES_DONE / 4, framesDone);
}

function publishLevelsReady(target: ArrayBufferLike, readyMask: number): void {
  const header = new Int32Array(target, 0, WAVEFORM_MIPMAP_HEADER.FRAMES_DONE / 4 + 1);
  Atomics.store(header, WAVEFORM_MIPMAP_HEADER.LEVELS_READY / 4, readyMask);
}

/**
 * Reads one bucket back. The reader half, here rather than in the renderer so both ends of the
 * layout live in one module and a Node round-trip test can exercise them against each other —
 * the same reasoning that put the SAB signal decode in `/platform/sab` at P0.7 (ADR-L2).
 */
export function readBucket(
  target: ArrayBufferLike,
  layout: WaveformMipmapLayout,
  levelIndex: number,
  bucket: number,
): { readonly min: number; readonly max: number; readonly rms: number } {
  const level = layout.levels[levelIndex];
  if (level === undefined) {
    throw new RangeError(`waveform mipmap: no level ${String(levelIndex)}`);
  }
  if (bucket < 0 || bucket >= level.bucketCount) {
    throw new RangeError(
      `waveform mipmap: bucket ${String(bucket)} out of range at level ${String(levelIndex)} (${String(level.bucketCount)} buckets)`,
    );
  }

  const view = new Float32Array(
    target,
    level.byteOffset,
    level.bucketCount * WAVEFORM_BUCKET_FLOATS,
  );
  const base = bucket * WAVEFORM_BUCKET_FLOATS;

  return {
    min: view[base] ?? 0,
    max: view[base + 1] ?? 0,
    rms: view[base + 2] ?? 0,
  };
}

/**
 * How many frames have been summarised so far, read atomically.
 *
 * The acquire half of the pair `publishFramesDone` releases. Cheap enough to call once per
 * animation frame, which is exactly what P2.5's progress line does — the same shape as the master
 * meter reading the signal ring and the bench reading its frame stats. **This is why there is no
 * progress `postMessage`**: the number is already in shared memory, and one atomic load per frame
 * costs less than one message per 65,536 frames.
 */
export function readFramesDone(target: ArrayBufferLike): number {
  const header = new Int32Array(target, 0, WAVEFORM_MIPMAP_HEADER.FRAMES_DONE / 4 + 1);
  return Atomics.load(header, WAVEFORM_MIPMAP_HEADER.FRAMES_DONE / 4);
}

/** The bitmask of levels safe to read. Pair with `isLevelReady` from the layout module. */
export function readLevelsReady(target: ArrayBufferLike): number {
  const header = new Int32Array(target, 0, WAVEFORM_MIPMAP_HEADER.FRAMES_DONE / 4 + 1);
  return Atomics.load(header, WAVEFORM_MIPMAP_HEADER.LEVELS_READY / 4);
}

/** Frames covered by a bucket, re-exported so callers do not need two imports to read a bucket. */
export { bucketFrameCount };
